import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";

const API_URL = process.env.REACT_APP_API_URL;
const S3_BASE_URL =
  "https://instatenders-supplier-upload.s3.ap-south-1.amazonaws.com";

function parseApiResponse(raw) {
  let data = raw;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return raw;
    }
  }
  if (data?.body != null) {
    if (typeof data.body === "string") {
      try {
        return JSON.parse(data.body);
      } catch {
        return data.body;
      }
    }
    return data.body;
  }
  return data;
}

function parseJsonIfString(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeApiPayload(raw) {
  let obj = raw;
  if (!obj || typeof obj !== "object") return raw;
  obj = { ...obj };
  if (typeof obj.data === "string") {
    obj.data = parseJsonIfString(obj.data);
  }
  if (
    obj.data &&
    typeof obj.data === "object" &&
    typeof obj.data.data === "string"
  ) {
    obj.data = { ...obj.data, ...parseJsonIfString(obj.data.data) };
  }
  return obj;
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== "object") return false;
  const success = responseData.success;
  return (
    success === true ||
    success === "true" ||
    success === 1 ||
    success === "1"
  );
}

function buildS3Url(s3Key) {
  const key = String(s3Key || "").trim().replace(/^\/+/, "");
  if (!key) return "";
  if (/^https?:\/\//i.test(key)) return key;
  return `${S3_BASE_URL}/${key}`;
}

function getImageUrl(item) {
  if (!item) return "";
  if (typeof item === "string") return buildS3Url(item);
  return (
    item.url ||
    item.imageUrl ||
    item.publicUrl ||
    buildS3Url(item.s3Key || item.key)
  );
}

function getItemImages(item) {
  if (!item || typeof item !== "object") return [];
  const raw = Array.isArray(item.images) ? item.images : [];
  return raw
    .map((img) => {
      const url = getImageUrl(img);
      return url ? { url } : null;
    })
    .filter(Boolean);
}

function getItemAttachments(item) {
  if (!item || typeof item !== "object") return [];
  const raw = Array.isArray(item.attachments) ? item.attachments : [];
  return raw
    .map((att, index) => {
      const url =
        att?.url ||
        att?.publicUrl ||
        buildS3Url(att?.s3Key || att?.key || "");
      const name =
        att?.originalFileName ||
        att?.fileName ||
        att?.name ||
        (url ? url.split("/").pop() : `Attachment ${index + 1}`);
      return url || name
        ? {
            url,
            name,
            uploadedAt: att?.uploadedAt || "",
          }
        : null;
    })
    .filter(Boolean);
}

function displayValue(value) {
  if (value == null || value === "") return "-";
  return String(value);
}

function displayUser(ref) {
  if (!ref) return "-";
  if (typeof ref === "string") return ref;
  if (ref.name) return ref.name;
  if (ref.email) return ref.email;
  return "-";
}

function capitalizeStatus(value) {
  if (!value) return "-";
  const s = String(value);
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

function formatDateTime(value) {
  if (!value) return "-";
  const str = String(value).trim();
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return str || "-";

  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(d);

  const get = (type) => parts.find((p) => p.type === type)?.value || "";
  const dayPeriod = get("dayPeriod").toUpperCase();
  return `${get("day")}/${get("month")}/${get("year")}, ${get("hour")}:${get("minute")} ${dayPeriod}`;
}

function getCustomerPriceRows(item) {
  const list = Array.isArray(item?.customerPrices) ? item.customerPrices : [];
  return list.map((row, index) => {
    const customerRef = row?.customerId;
    const customerName =
      String(row?.customerName || "").trim() ||
      (customerRef && typeof customerRef === "object"
        ? String(customerRef.customerName || "").trim()
        : "") ||
      "-";
    const currency = String(row?.Currency || row?.priceType || "").trim() || "-";
    const partNumber = String(row?.partNumber || "").trim() || "-";
    const customerMaterialCode =
      String(row?.customerMaterialCode || "").trim() || "-";
    const customerMaterialDescription =
      String(row?.customerMaterialDescription || "").trim() || "-";
    const price =
      row?.price == null || row?.price === ""
        ? "-"
        : Number.isFinite(Number(row.price))
          ? Number(row.price)
          : row.price;
   const deliveryLeadTime = String(row?.deliveryLeadTime ?? "").trim();

    return {
      key: String(row?._id || row?.id || index),
      customerName,
      currency,
      partNumber,
      customerMaterialCode,
      customerMaterialDescription,
      price,
      deliveryLeadTime,

    };
  });
}

const ViewField = ({ label, children }) => (
  <div className="form-group row align-items-center">
    <label className="col-12 col-md-4 col-form-label">{label}</label>
    <div className="col-12 col-md-8">
      <div className="txtdsply">{children}</div>
    </div>
  </div>
);

const SectionTitle = ({ icon, title }) => (
  <div className="row mb-2 mt-3">
    <div className="col-12">
      <h5 className="text-primary border-bottom pb-2 mb-3">
        <i className={`fa-solid ${icon} mr-2`} />
        {title}
      </h5>
    </div>
  </div>
);

function ECatalogueView() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchEcatalogueById = useCallback(async () => {
    if (!id) {
      setItem(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(API_URL, {
        action: "get-ecatalogue-by-id",
        id,
      });
      const responseData = normalizeApiPayload(parseApiResponse(response.data));
      const detail =
        responseData?.data && typeof responseData.data === "object"
          ? responseData.data
          : null;

      if (isApiSuccess(responseData) && detail) {
        setItem(detail);
      } else {
        setItem(null);
        toast.error(
          responseData?.message || "Failed to fetch e-catalogue details",
          { autoClose: 2000 },
        );
      }
    } catch (error) {
      console.error("Fetch e-catalogue by id error:", error);
      setItem(null);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        (typeof errData === "object" && errData?.message) ||
          "An error occurred while fetching e-catalogue details",
        { autoClose: 2000 },
      );
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchEcatalogueById();
  }, [fetchEcatalogueById]);

  const header = (
    <div className="content-header">
      <div className="container-fluid">
        <div className="row mb-2 align-items-center">
          <div className="col-6 col-sm-6">
            <h1 className="m-0 txt_lft">View E-Catalogue</h1>
          </div>
          <div className="col-6 col-sm-6">
            <ol className="breadcrumb justify-content-end mb-0">
              <li className="breadcrumb-item">
                <Link to="/ecatalogue">
                  <i className="fa-solid fa-arrow-left-long" /> Back
                </Link>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div>
        {header}
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div>
        {header}
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <p>E-catalogue not found</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const images = getItemImages(item);
  const attachments = getItemAttachments(item);
  const customerPriceRows = getCustomerPriceRows(item);
  const materialRef =
    item.materialRef && typeof item.materialRef === "object"
      ? item.materialRef
      : null;

  return (
    <div>
      {header}
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <div className="card-body">
                    <SectionTitle icon="fa-book-open" title="Catalogue Details" />

                    <ViewField label="Material Code">
                      {displayValue(item.materialCode)}
                    </ViewField>
                    <ViewField label="Product Name">
                      {displayValue(item.productName)}
                    </ViewField>
                    <ViewField label="Material Description">
                      {displayValue(item.materialDescription)}
                    </ViewField>
                    <ViewField label="Category">
                      {displayValue(item.categoryName)}
                    </ViewField>
                    <ViewField label="Subcategory">
                      {displayValue(item.subcategoryName)}
                    </ViewField>
                    <ViewField label="HSN Code">
                      {displayValue(item.hsnCode)}
                    </ViewField>
                    <ViewField label="UOM">{displayValue(item.uom)}</ViewField>
                     <ViewField label="Stock Quantity">{displayValue(item.stockQuantity)}</ViewField>
                    <ViewField label="Stock Category">
                      {displayValue(item.stockCategory)}
                    </ViewField>
                    <ViewField label="Part Number">
                      {displayValue(item.partNumber || item.materialPartNumber)}
                    </ViewField>
                    <ViewField label="Make / Brand / OEM">
                      {displayValue(item.makeBrandOem)}
                    </ViewField>
                    <ViewField label="Status">
                      <span
                        className={`badge ${
                          String(item.status).toLowerCase() === "active"
                            ? "badge-success"
                            : String(item.status).toLowerCase() === "inactive"
                              ? "badge-danger"
                              : "badge-secondary"
                        }`}
                        style={{ fontSize: "90%", fontWeight: 550 }}
                      >
                        {capitalizeStatus(item.status)}
                      </span>
                    </ViewField>

                    <SectionTitle icon="fa-users" title="Customer Details" />
                    {customerPriceRows.length === 0 ? (
                      <div className="txtdsply text-muted mb-3">
                        No Customer Details added yet.
                      </div>
                    ) : (
                      <div className="table-responsive mb-3">
                        <table className="table table-bordered table-sm mb-0">
                          <thead>
                            <tr
                              style={{
                                backgroundColor: "#003366",
                                color: "#fff",
                              }}
                            >
                              <th
                                style={{
                                  fontSize: "12px",
                                  width: "48px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                S.No
                              </th>
                              <th
                                style={{
                                  fontSize: "12px",
                                  whiteSpace: "nowrap",
                                  minWidth: "160px",
                                }}
                              >
                                Customer
                              </th>
                              <th
                                style={{
                                  fontSize: "12px",
                                  width: "80px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Currency
                              </th>
                              <th
                                style={{
                                  fontSize: "12px",
                                  whiteSpace: "nowrap",
                                  minWidth: "120px",
                                }}
                              >
                                Customer Material Code
                              </th>
                              <th
                                style={{
                                  fontSize: "12px",
                                  whiteSpace: "nowrap",
                                  minWidth: "140px",
                                }}
                              >
                                Customer Material Description
                              </th>
                              <th
                                style={{
                                  fontSize: "12px",
                                  width: "80px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Price
                              </th>
                               <th
                                style={{
                                  fontSize: "12px",
                                  width: "80px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Delivery Lead Time
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {customerPriceRows.map((row, index) => (
                              <tr key={row.key}>
                                <td>{index + 1}</td>
                                <td>{row.customerName}</td>
                                <td>{row.currency}</td>
                                <td>{row.customerMaterialCode}</td>
                                <td>{row.customerMaterialDescription}</td>
                                <td style={{ fontWeight: 600 }}>{row.price}</td>
                                <td>{displayValue(row.deliveryLeadTime)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {materialRef ? (
                      <>
                        <SectionTitle
                          icon="fa-cubes"
                          title="Material Reference"
                        />
                        <ViewField label="Material Code">
                          {displayValue(materialRef.materialCode)}
                        </ViewField>
                        <ViewField label="Product Name">
                          {displayValue(materialRef.productName)}
                        </ViewField>
                        <ViewField label="Status">
                          {capitalizeStatus(materialRef.status)}
                        </ViewField>
                      </>
                    ) : null}

                    <SectionTitle icon="fa-image" title="Images" />
                    {images.length === 0 ? (
                      <div className="txtdsply text-muted mb-3">No images</div>
                    ) : (
                      <div
                        className="d-flex flex-wrap mb-3"
                        style={{ gap: "10px" }}
                      >
                        {images.map((img, index) => (
                          <a
                            key={`${img.url}-${index}`}
                            href={img.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              width: "110px",
                              height: "110px",
                              border: "1px solid #e3e8ee",
                              borderRadius: "8px",
                              overflow: "hidden",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "#f8fafb",
                            }}
                          >
                            <img
                              src={img.url}
                              alt={`catalogue-${index + 1}`}
                              style={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                              }}
                            />
                          </a>
                        ))}
                      </div>
                    )}

                    <SectionTitle icon="fa-paperclip" title="Attachments" />
                    {attachments.length === 0 ? (
                      <div className="txtdsply text-muted mb-3">
                        No attachments
                      </div>
                    ) : (
                      <div className="table-responsive mb-3">
                        <table className="table table-bordered table-sm mb-0">
                          <thead>
                            <tr
                              style={{
                                backgroundColor: "#003366",
                                color: "#fff",
                              }}
                            >
                              <th style={{ fontSize: "12px", width: "48px" }}>
                                S.No
                              </th>
                              <th style={{ fontSize: "12px" }}>File Name</th>
                              <th style={{ fontSize: "12px", width: "180px" }}>
                                Uploaded At
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {attachments.map((att, index) => (
                              <tr key={`${att.name}-${index}`}>
                                <td>{index + 1}</td>
                                <td>
                                  {att.url ? (
                                    <a
                                      href={att.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {att.name}
                                    </a>
                                  ) : (
                                    att.name
                                  )}
                                </td>
                                <td>{formatDateTime(att.uploadedAt)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <SectionTitle icon="fa-clock" title="Audit" />
                    <ViewField label="Created By">
                      {displayUser(item.createdBy || item.userId)}
                    </ViewField>
                    <ViewField label="Updated By">
                      {displayUser(item.updatedBy)}
                    </ViewField>
                    <ViewField label="Created At">
                      {formatDateTime(item.createdAt)}
                    </ViewField>
                    <ViewField label="Updated At">
                      {formatDateTime(item.updatedAt)}
                    </ViewField>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ECatalogueView;
